import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// --- Types ---
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
// --- Name & City pools ---
const NAMES = ['María', 'Ana', 'Lucía', 'Carmen', 'Rosa', 'Elena', 'Patricia', 'Gabriela', 'Isabel', 'Sofía', 'Laura', 'Andrea', 'Diana', 'Claudia', 'Alejandra', 'Daniela', 'Valentina', 'Fernanda', 'Adriana', 'Mónica', 'Silvia', 'Lorena', 'Beatriz', 'Verónica', 'Natalia', 'Catalina', 'Paola', 'Mariana', 'Camila', 'Karla', 'Jessica', 'Yolanda', 'Sandra', 'Lina', 'Gloria', 'Teresa', 'Rocío', 'Estela', 'Marisol', 'Leticia'];
const CITIES = ['Miami, FL', 'Houston, TX', 'Los Angeles, CA', 'Chicago, IL', 'Dallas, TX', 'San Antonio, TX', 'Phoenix, AZ', 'Orlando, FL', 'New York, NY', 'Denver, CO', 'Las Vegas, NV', 'Atlanta, GA', 'Tampa, FL', 'San Diego, CA', 'Austin, TX', 'Charlotte, NC', 'Sacramento, CA', 'El Paso, TX', 'Tucson, AZ', 'Albuquerque, NM', 'Riverside, CA', 'Fresno, CA', 'Mesa, AZ', 'Bakersfield, CA', 'Fort Worth, TX'];
const DEFAULT_PRODUCTS: ScannedProduct[] = [{
  title: 'Método Anti-Inflamatorio 7D',
  editionLabel: 'Guía Digital',
  priceNumber: 6.36,
  priceText: '$6.36',
  thumbnailUrl: ''
}];
// --- DOM Scanner ---
function scanProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // 1. Try JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' || data.offers) {
          const offer = data.offers?.[0] || data.offers || {};
          products.push({
            title: data.name || 'Product',
            editionLabel: data.category || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: offer.price ? `$${parseFloat(offer.price).toFixed(2)}` : '',
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {
        /* skip malformed JSON-LD */
      }
    });
    if (products.length > 0) return products;
    // 2. Scan DOM — look for the primary offer price near CTA buttons
    let thumbnailUrl = '';
    const heroImg = document.querySelector('img[fetchpriority="high"]') as HTMLImageElement;
    if (heroImg) {
      thumbnailUrl = heroImg.src;
    } else {
      const firstLargeImg = document.querySelector('img[width="1024"], img.rounded-3xl') as HTMLImageElement;
      if (firstLargeImg) thumbnailUrl = firstLargeImg.src;
    }
    // Find price from CTA buttons (most reliable — the buy button contains the real price)
    const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/;
    const ctaLinks = document.querySelectorAll('a[href*="cart"], a[href*="checkout"], button.shimmer-btn, a.shimmer-btn');
    let offerPrice = 0;
    let offerPriceText = '';
    ctaLinks.forEach(el => {
      if (offerPrice > 0) return;
      const match = el.textContent?.match(priceRegex);
      if (match) {
        offerPrice = parseFloat(match[1]);
        offerPriceText = `${offerPrice.toFixed(2)}`;
      }
    });
    // Fallback: look for the large price display in the offer section
    if (offerPrice === 0) {
      const offerSection = document.getElementById('offer');
      if (offerSection) {
        const match = offerSection.textContent?.match(priceRegex);
        if (match) {
          offerPrice = parseFloat(match[1]);
          offerPriceText = `${offerPrice.toFixed(2)}`;
        }
      }
    }
    if (offerPrice > 0) {
      products.push({
        title: 'Método Anti-Inflamatorio 7D',
        editionLabel: 'Guía Digital',
        priceNumber: offerPrice,
        priceText: offerPriceText,
        thumbnailUrl
      });
    }
  } catch {
    /* scanner failed, will use defaults */
  }
  return products.length > 0 ? products : DEFAULT_PRODUCTS;
}
// --- Helpers ---
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return (Math.random() * 7 + 8) * 1000; // 8–15 seconds
}
function pickName(history: string[]): string {
  const available = NAMES.filter(n => !history.includes(n));
  if (available.length === 0) return pickRandom(NAMES);
  return pickRandom(available);
}
// --- Check reduced motion ---
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
// --- Component ---
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const reduced = prefersReducedMotion();
  const showNotification = useCallback(() => {
    const products = productsRef.current;
    if (products.length === 0) return;
    const name = pickName(nameHistoryRef.current);
    nameHistoryRef.current = [...nameHistoryRef.current.slice(-9), name];
    const product = pickRandom(products);
    // Ensure thumbnail fallback
    const finalProduct = {
      ...product,
      thumbnailUrl: product.thumbnailUrl || 'https://cdn.magicpatterns.com/uploads/aoUnQchMG3dqd9JvoHVniN/heylloyd-image-1775268223226_(2).webp'
    };
    idRef.current += 1;
    setNotification({
      id: idRef.current,
      name,
      city: pickRandom(CITIES),
      product: finalProduct
    });
    // Hide after 4 seconds
    setTimeout(() => {
      setNotification(null);
    }, 4000);
    // Schedule next
    timerRef.current = setTimeout(showNotification, getRandomInterval() + 4000);
  }, []);
  useEffect(() => {
    // Scan products on mount
    productsRef.current = scanProducts();
    // First notification after 3 seconds
    timerRef.current = setTimeout(showNotification, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNotification]);
  const variants = reduced ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.95
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] pointer-events-none" data-id="element-284">
      <AnimatePresence mode="wait" data-id="element-285">
        {notification && <motion.div key={notification.id} initial={variants.initial} animate={variants.animate} exit={variants.exit} transition={{
        duration: reduced ? 0.15 : 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-2xl shadow-lg shadow-black/10 border border-black/5 px-4 py-3 flex items-center gap-3 max-w-[360px] w-max" data-id="element-286">
            <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-11 h-11 rounded-xl object-cover shrink-0 border border-black/5" data-id="element-287" />
            <div className="min-w-0" data-id="element-288">
              <p className="text-sm text-charcoal leading-snug" data-id="element-289">
                <span className="font-semibold" data-id="element-290">{notification.name}</span>
                {' en '}
                <span className="text-charcoal/70" data-id="element-291">{notification.city}</span>
              </p>
              <p className="text-xs text-charcoal/60 leading-snug mt-0.5" data-id="element-292">
                compró{' '}
                <span className="font-medium text-charcoal/80" data-id="element-293">
                  {notification.product.editionLabel || notification.product.title}
                </span>
                {' — '}
                <span className="font-semibold text-sage" data-id="element-294">
                  {notification.product.priceText}
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}